import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Compass, Sparkles } from 'lucide-react';
import { QuizResults } from '@/components/quiz/QuizResults';
import { v4 as uuidv4 } from 'uuid';

interface QuizQuestion {
  id: string;
  question: string;
  options: {
    label: string;
    value: string;
    tags: string[];
    emoji: string;
  }[];
}

const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: 'travel_style',
    question: 'What\'s your ideal travel vibe?',
    options: [
      { label: 'Adventure & Thrills', value: 'adventure', tags: ['Adventure', 'Hiking', 'Outdoor'], emoji: '🏔️' },
      { label: 'Relaxation & Wellness', value: 'relaxation', tags: ['Beach', 'Spa', 'Wellness'], emoji: '🏖️' },
      { label: 'Culture & History', value: 'culture', tags: ['Culture', 'History', 'Museums'], emoji: '🏛️' },
      { label: 'Food & Nightlife', value: 'foodie', tags: ['Food', 'Nightlife', 'City'], emoji: '🍜' },
    ],
  },
  {
    id: 'budget',
    question: 'What\'s your travel budget style?',
    options: [
      { label: 'Budget Backpacker', value: 'budget', tags: ['Budget', 'Hostel'], emoji: '🎒' },
      { label: 'Mid-Range Comfort', value: 'mid', tags: ['Mid-Range', 'Hotel'], emoji: '🏨' },
      { label: 'Luxury All The Way', value: 'luxury', tags: ['Luxury', 'Resort'], emoji: '✨' },
      { label: 'Mix It Up', value: 'mixed', tags: ['Flexible'], emoji: '🔀' },
    ],
  },
  {
    id: 'activity_level',
    question: 'How active do you want to be?',
    options: [
      { label: 'Non-stop Explorer', value: 'high', tags: ['Active', 'Adventure', 'Hiking'], emoji: '🏃' },
      { label: 'Balanced Mix', value: 'medium', tags: ['Balanced', 'Sightseeing'], emoji: '🚶' },
      { label: 'Slow & Steady', value: 'low', tags: ['Relaxing', 'Slow Travel'], emoji: '🧘' },
      { label: 'Lazy Days Only', value: 'minimal', tags: ['Beach', 'Resort', 'Spa'], emoji: '😴' },
    ],
  },
  {
    id: 'trip_duration',
    question: 'How long is your ideal trip?',
    options: [
      { label: 'Weekend Escape (2-4 days)', value: 'short', tags: ['Weekend', 'City Break'], emoji: '⚡' },
      { label: 'Week-long Adventure (5-7 days)', value: 'week', tags: ['Week Trip'], emoji: '📅' },
      { label: 'Extended Journey (2+ weeks)', value: 'long', tags: ['Long Trip', 'Multi-Destination'], emoji: '🗺️' },
      { label: 'Digital Nomad Style (1+ month)', value: 'extended', tags: ['Long Stay', 'Slow Travel'], emoji: '💻' },
    ],
  },
  {
    id: 'companion',
    question: 'Who are you traveling with?',
    options: [
      { label: 'Solo Adventure', value: 'solo', tags: ['Solo Travel'], emoji: '🎯' },
      { label: 'Romantic Getaway', value: 'couple', tags: ['Couples', 'Romantic'], emoji: '💑' },
      { label: 'Friends Trip', value: 'friends', tags: ['Group Travel', 'Party'], emoji: '👯' },
      { label: 'Family Fun', value: 'family', tags: ['Family', 'Kid-Friendly'], emoji: '👨‍👩‍👧‍👦' },
    ],
  },
];

export default function TravelQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, { value: string; tags: string[] }>>({});
  const [showResults, setShowResults] = useState(false);
  const [sessionId] = useState(() => uuidv4());

  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;
  const question = QUIZ_QUESTIONS[currentQuestion];

  const handleAnswer = (option: QuizQuestion['options'][0]) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: { value: option.value, tags: option.tags },
    }));

    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQuestion((prev) => prev + 1), 300);
    } else {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
  };

  // Collect all selected tags
  const allTags = Object.values(answers).flatMap((a) => a.tags);

  if (showResults) {
    return (
      <QuizResults
        answers={answers}
        tags={allTags}
        sessionId={sessionId}
        onRestart={handleRestart}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Travel Style Quiz | Find Your Perfect Trip</title>
        <meta name="description" content="Discover your travel personality and get personalized destination recommendations with our fun travel style quiz." />
      </Helmet>

      <div className="container px-4 sm:px-6 mx-auto py-8 max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Compass className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">Travel Quiz</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">Discover Your Travel Style</h1>
          <p className="text-muted-foreground">
            Answer {QUIZ_QUESTIONS.length} quick questions to get personalized recommendations
          </p>
        </div>

        <div className="mb-6">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Question {currentQuestion + 1} of {QUIZ_QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-6 text-center">{question.question}</h2>
            
            <div className="grid gap-3 sm:grid-cols-2">
              {question.options.map((option) => {
                const isSelected = answers[question.id]?.value === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option)}
                    className={`p-4 rounded-lg border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                      isSelected ? 'border-primary bg-primary/10' : 'border-border'
                    }`}
                  >
                    <span className="text-2xl mb-2 block">{option.emoji}</span>
                    <span className="font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentQuestion === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {answers[question.id] && currentQuestion < QUIZ_QUESTIONS.length - 1 && (
            <Button onClick={() => setCurrentQuestion((prev) => prev + 1)}>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          
          {answers[question.id] && currentQuestion === QUIZ_QUESTIONS.length - 1 && (
            <Button onClick={() => setShowResults(true)}>
              <Sparkles className="w-4 h-4 mr-2" />
              See Results
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
